import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, XCircle, ShieldAlert, Wallet, ChevronRight, X } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AccountStatusBanner({ onStatusLoaded }) {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    const fetchStatus = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/account/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatus(res.data);
        if (onStatusLoaded) onStatusLoaded(res.data);
      } catch (error) {
        console.error('Failed to fetch account status:', error);
      }
    };
    fetchStatus();
  }, [token, onStatusLoaded]);

  if (!status || status.warnings.length === 0) return null;

  const handleAction = (action) => {
    switch (action) {
      case 'verify_identity':
        navigate('/verification');
        break;
      case 'topup_wallet':
        navigate(user?.role === 'provider' ? '/provider/earnings' : '/wallet');
        break;
      default:
        break;
    }
  };

  const dismissWarning = (type) => {
    setDismissed(prev => ({ ...prev, [type]: true }));
  };

  const activeWarnings = status.warnings.filter(w => !dismissed[w.type]);
  if (activeWarnings.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="account-status-banner">
      {activeWarnings.map((warning, idx) => {
        const isCritical = warning.severity === 'critical';
        const isWallet = warning.type === 'wallet_balance';
        const isVerification = warning.type === 'identity_verification';
        
        return (
          <div 
            key={warning.type || `warning-${idx}`}
            className={`relative rounded-xl p-4 ${
              isCritical 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}
            data-testid={`warning-${warning.type}`}
          >
            {/* Dismiss button for non-critical warnings */}
            {!isCritical && (
              <button 
                onClick={() => dismissWarning(warning.type)}
                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                isCritical ? 'bg-red-100' : 'bg-amber-100'
              }`}>
                {isWallet ? (
                  <Wallet className={`w-5 h-5 ${isCritical ? 'text-red-600' : 'text-amber-600'}`} />
                ) : isVerification ? (
                  <ShieldAlert className={`w-5 h-5 ${isCritical ? 'text-red-600' : 'text-amber-600'}`} />
                ) : (
                  <AlertTriangle className={`w-5 h-5 ${isCritical ? 'text-red-600' : 'text-amber-600'}`} />
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className={`font-semibold text-sm ${isCritical ? 'text-red-800' : 'text-amber-800'}`}>
                  {isWallet ? 'Wallet Balance Issue' : isVerification ? 'Identity Verification Required' : 'Action Required'}
                </h4>
                <p className={`text-sm mt-0.5 ${isCritical ? 'text-red-700' : 'text-amber-700'}`}>
                  {warning.message}
                </p>
                
                {/* Verification Guide */}
                {isVerification && (
                  <div className={`mt-2 p-2 rounded-lg text-xs ${isCritical ? 'bg-red-100' : 'bg-amber-100'}`}>
                    <p className="font-medium mb-1">How to verify:</p>
                    <ol className="list-decimal list-inside space-y-0.5 text-slate-600">
                      <li>Go to Account Settings</li>
                      <li>Upload a clear photo of your valid ID</li>
                      <li>We'll verify your name using OCR technology</li>
                      <li>Verification is usually instant</li>
                    </ol>
                  </div>
                )}
                
                {/* Action Button */}
                {warning.action && (
                  <Button
                    size="sm"
                    onClick={() => handleAction(warning.action)}
                    className={`mt-3 ${
                      isCritical 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-amber-500 hover:bg-amber-600 text-slate-900'
                    }`}
                    data-testid={`action-${warning.action}`}
                  >
                    {warning.action_label}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Blocked indicator for critical warnings */}
            {isCritical && (
              <div className="mt-3 pt-3 border-t border-red-200 flex items-center gap-2 text-red-700 text-xs">
                <XCircle className="w-4 h-4" />
                <span>
                  {isWallet 
                    ? 'Your profile is hidden from customers. Minimum ₱100 balance required.'
                    : 'Some features are restricted until verified'
                  }
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
